import React from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import { compose, withProps } from 'recompose';
import {
  reduxForm,
  formPropTypes,
  SubmissionError,
  getFormValues,
  getFormSyncErrors,
  getFormAsyncErrors,
  getFormSubmitErrors,
  isDirty,
  isPristine,
  isValid,
  isInvalid,
  isSubmitting,
  hasSubmitSucceeded,
  hasSubmitFailed,
} from 'redux-form';
import { Button, ButtonToolbar } from 'react-bootstrap';


import TicketVariables, {
  ticketIdDataSelector,
  ticketVariablesDataSelector,
  formValuesToInput,
  validate,
} from '../../forms/ticketVariablesEditor/form';


import queryMetaHandler from '../../../utils/queryMetaHandler';
import omitProps from '../../../utils/omitProps';
import { JobFactsTicketFragment } from './JobFacts';
import { GetTicketCalculatedFieldsQuery } from './TicketCalculatedFields';

const FORM_NAME = 'ticketVariablesEditor';

export const getFormState = (state) => {
  return {
    values: getFormValues(FORM_NAME)(state),
    syncErrors: getFormSyncErrors(FORM_NAME)(state),
    asyncErrors: getFormAsyncErrors(FORM_NAME)(state),
    submitErrors: getFormSubmitErrors(FORM_NAME)(state),
    dirty: isDirty(FORM_NAME)(state),
    pristine: isPristine(FORM_NAME)(state),
    valid: isValid(FORM_NAME)(state),
    invalid: isInvalid(FORM_NAME)(state),
    submitting: isSubmitting(FORM_NAME)(state),
    submitSucceeded: hasSubmitSucceeded(FORM_NAME)(state),
    submitFailed: hasSubmitFailed(FORM_NAME)(state),
  };
};


const propTypes = exact({
  disabled: PropTypes.bool,
  ticketId: PropTypes.number.isRequired,
  ticket: PropTypes.object.isRequired,
  showBillingHours: PropTypes.bool.isRequired,
  mutate: PropTypes.func.isRequired,
  ...formPropTypes,
  /* I believe the following props are redux props that are not found within the formPropTypes */
  validate: PropTypes.func.isRequired,
  clearFields: PropTypes.any.isRequired,
  clearSubmitErrors: PropTypes.any.isRequired,
});


const defaultProps = {
  disabled: false,
};

const TicketVariablesEditor = (props) => {
  const {
    disabled,
    ticketId,
    mutate,
    showBillingHours,
    handleSubmit,
    pristine,
    submitting,
    error,
    ticket,
  } = props;

  const { workedHoursEqualsBilling } = ticket;

  // FIXME: Factor submission handler out of component
  const updateTicketVariables = async (values) => {
    const variables = {
      input: {
        id: ticketId,
        ...formValuesToInput(values),
      },
    };
    try {
      await mutate({ variables });
    } catch (e) {
      const message = e.graphQLErrors.reduce((errorMessage, gqlError) => {
        return errorMessage + gqlError.details;
      }, '');
      throw new SubmissionError({ _error: message });
    }
  };

  return (
    <form onSubmit={handleSubmit(updateTicketVariables)}>
      <TicketVariables
        disabled={disabled}
        showBillingHours={showBillingHours}
        workedHoursEqualsBilling={workedHoursEqualsBilling}
      />
      {!pristine
      && (
      <ButtonToolbar className="pull-right">
        <Button
          bsStyle="primary"
          disabled={submitting}
          type="submit"
        >Save
        </Button>
      </ButtonToolbar>
      )}
      {error
      && <span>{error}</span>}
    </form>
  );
};


TicketVariablesEditor.propTypes = propTypes;


TicketVariablesEditor.defaultProps = defaultProps;


export const TicketVariablesFragment = gql`
  fragment TicketVariables on Ticket {
    id
    department {
      id
      name
      has_hourly_billing
    }
    billing_revenue
    billing_hours
    supplemental_info
    state
    workedHoursEqualsBilling: worked_hours_equals_billing
    job {
      id
      all_reviewed
      total_billing_revenue
      total_billing_time {
        to_hours
      }
      total_worked_hours {
        to_string
      }
    }
  }
`;


const Query = gql`
  query SomeTicket($ticketId: Int!) {
    ticket(ticket_id: $ticketId) {
      ...TicketVariables
      ...JobFactsTicketFragment
    }
  }
  ${TicketVariablesFragment}
  ${JobFactsTicketFragment}
`;


/*
* input: TicketVariablesUpdateInput!
*   id: Int!
*   billing_hours: Float
*   billing_revenue: Float
*   supplemental_info: String
 */
const Mutation = gql`
  mutation TicketVariablesUpdate($input: TicketVariablesUpdateInput!) {
    ticket_variables_update(input: $input) {
      ...TicketVariables
      ...JobFactsTicketFragment
    }
  }
  ${TicketVariablesFragment}
  ${JobFactsTicketFragment}
`;


const withData = graphql(Query, {
  options: { fetchPolicy: 'network-only' },
});


const withMutation = graphql(Mutation, {
  options: (props) => {
    return {
      refetchQueries: [
        {
          query: GetTicketCalculatedFieldsQuery,
          variables: {
            ticketId: props.ticketId,
          },
        },
      ],
    };
  },
});


const withForm = reduxForm({
  form: FORM_NAME,
  validate,
  enableReinitialize: true,
});


const mapDataToProps = withProps((props) => {
  const { data } = props;
  const { ticket } = data;
  const { has_hourly_billing: showBillingHours } = ticket.department;

  return {
    ticketId: ticketIdDataSelector(data),
    initialValues: ticketVariablesDataSelector(data),
    ticket,
    showBillingHours,
  };
});


const enhance = compose(
  withMutation,
  withData,
  queryMetaHandler,
  mapDataToProps,
  omitProps(['data']),
  withForm,
);


export default enhance(TicketVariablesEditor);
